// Centralised "first-data" detection used by the dashboard checklist (B2-02),
// the sidebar pulse (B2-05) and the empty-state migration banners (B2-06).
//
// Each flag is true the moment the qualifying action has been completed at
// least once for this nursery, it never reverts to false, even if data is
// later archived. This is deliberately a one-time activation indicator.

import { useMemo } from "react";
import { startOfWeek, addDays, format } from "date-fns";
import { useChildren } from "@/hooks/useChildren";
import { useStaff } from "@/hooks/useStaff";
import { useShifts } from "@/hooks/useShifts";

export interface OnboardingProgress {
  hasAnyChild: boolean;
  hasAnyStaff: boolean;
  hasInvitedTeammate: boolean;
  hasAnyShift: boolean;
  isLoading: boolean;
  allComplete: boolean;
}

export function useOnboardingProgress(): OnboardingProgress {
  // Pull EVERYTHING (including archived) so the flags only ever ratchet
  // forward: a nursery that adds and then archives one child still keeps
  // its "started" status.
  const { data: childrenData, isLoading: cLoad } = useChildren();
  const { data: staffData, isLoading: sLoad } = useStaff();

  const today = new Date();
  const weekStart = startOfWeek(today, { weekStartsOn: 1 });
  const weekEnd = addDays(weekStart, 6);
  const { data: weekShifts, isLoading: shLoad } = useShifts(
    format(weekStart, "yyyy-MM-dd"),
    format(weekEnd, "yyyy-MM-dd"),
  );

  return useMemo(() => {
    const hasAnyChild = (childrenData?.length ?? 0) > 0;
    const hasAnyStaff = (staffData?.length ?? 0) > 0;
    const hasInvitedTeammate = (staffData ?? []).some((s) => !!s.user_id);
    const hasAnyShift = (weekShifts?.length ?? 0) > 0;
    return {
      hasAnyChild,
      hasAnyStaff,
      hasInvitedTeammate,
      hasAnyShift,
      isLoading: cLoad || sLoad || shLoad,
      allComplete: hasAnyChild && hasAnyStaff && hasInvitedTeammate && hasAnyShift,
    };
  }, [childrenData, staffData, weekShifts, cLoad, sLoad, shLoad]);
}
